import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthSearchService, GrowthSeoProfile } from './growth-search.service';
import { GrowthDistributionService } from './growth-distribution.service';
import { GrowthMarketingService, GrowthContentAsset } from './growth-marketing.service';

interface TextRow {
  value: string;
}

interface KeyValueRow {
  key: string;
  value: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-seo-profiles',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    OfferingSelectorComponent,
    GrowthContentAssetSelectorComponent,
    RouterLink,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    SelectModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.search.seo.title')" [description]="t('growth.search.seo.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.search.seo.hint.title'"
        [descriptionKey]="'growth.search.seo.hint.description'"
        [bulletKeys]="[
          'growth.search.seo.hint.bullets.audit',
          'growth.search.seo.hint.bullets.schema',
          'growth.search.seo.hint.bullets.sandbox',
          'growth.search.seo.hint.bullets.storefrontBoundary'
        ]" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.search.seo.actions.newProfile')" (click)="openDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
          <a routerLink="/marketplace/storefront/publishing" pButton severity="help" [label]="t('growth.search.seo.actions.openStorefrontPublishing')"></a>
        </div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else {
          <p-table [value]="profiles()" [rows]="10" [paginator]="true">
            <ng-template pTemplate="header"><tr><th>{{ t('growth.search.seo.fields.title') }}</th><th>{{ t('growth.search.seo.fields.targetType') }}</th><th>{{ t('growth.search.seo.fields.status') }}</th><th>{{ t('growth.intelligence.dashboard.columns.score') }}</th><th>{{ t('growth.strategy.columns.actions') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.title }}</td>
                <td>{{ targetTypeLabel(row.targetType) }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.score ?? '-' }}</td>
                <td class="actions">
                  <button pButton type="button" size="small" severity="secondary" [label]="t('common.edit')" (click)="edit(row)"></button>
                  <button pButton type="button" size="small" [label]="t('growth.search.seo.actions.runAudit')" (click)="runAudit(row)"></button>
                  <button pButton type="button" size="small" severity="help" [label]="t('growth.search.seo.actions.createStorefrontApply')" (click)="createStorefrontApply(row)"></button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <p-dialog [header]="t('growth.search.seo.dialog.title')" [visible]="dialogVisible()" (visibleChange)="dialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(860px,95vw)' }">
        <ain-info-hint
          [titleKey]="'growth.search.seo.help.storefrontBoundary.title'"
          [descriptionKey]="'growth.search.seo.help.storefrontBoundary.description'" />

        <div class="grid">
          <ain-form-field [label]="t('growth.search.seo.fields.title')"><input pInputText [(ngModel)]="model.title" /></ain-form-field>
          <ain-form-field [label]="t('growth.search.seo.fields.targetType')">
            <p-select [options]="targetTypeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="model.targetType" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.search.seo.fields.targetRefId')">
            @if (model.targetType === 'product') {
              <ain-offering-selector [(ngModel)]="model.targetRefId" />
            } @else if (model.targetType === 'content-asset' || model.targetType === 'landing-page') {
              <ain-growth-content-asset-selector [(ngModel)]="model.targetRefId" />
            } @else {
              <ain-info-hint
                [titleKey]="'growth.search.seo.help.collectionTarget.title'"
                [descriptionKey]="'growth.search.seo.help.collectionTarget.description'" />
              <input pInputText [(ngModel)]="model.targetRefId" [placeholder]="t('growth.search.seo.fields.collectionRefPlaceholder')" />
            }
          </ain-form-field>
          <ain-form-field [label]="t('growth.search.seo.fields.status')">
            <p-select [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="model.status" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.search.seo.fields.description')"><textarea pInputTextarea rows="3" [(ngModel)]="model.description"></textarea></ain-form-field>
        </div>

        <div class="section-title"><h4>{{ t('growth.search.seo.sections.keywords') }}</h4></div>
        @for (row of keywordRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.search.seo.fields.keyword')"><input pInputText [(ngModel)]="row.value" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeKeyword($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addKeyword()"></button>

        <div class="section-title"><h4>{{ t('growth.search.seo.sections.meta') }}</h4></div>
        @for (row of metaRows; track $index) {
          <div class="row-grid meta-row">
            <ain-form-field [label]="t('growth.search.seo.fields.metaKey')"><input pInputText [(ngModel)]="row.key" /></ain-form-field>
            <ain-form-field [label]="t('growth.search.seo.fields.metaValue')"><input pInputText [(ngModel)]="row.value" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeMeta($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addMeta()"></button>

        <div class="section-title"><h4>{{ t('growth.search.seo.sections.schema') }}</h4></div>
        @for (row of schemaRows; track $index) {
          <div class="row-grid meta-row">
            <ain-form-field [label]="t('growth.search.seo.fields.schemaKey')"><input pInputText [(ngModel)]="row.key" /></ain-form-field>
            <ain-form-field [label]="t('growth.search.seo.fields.schemaValue')"><input pInputText [(ngModel)]="row.value" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeSchema($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addSchema()"></button>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit,minmax(220px,1fr)); }
    .toolbar,.actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .section-title { margin-top: 8px; }
    .row-grid { display: grid; gap: 8px; grid-template-columns: minmax(180px,1fr) auto; align-items: end; margin-bottom: 8px; }
    .meta-row { grid-template-columns: minmax(180px,1fr) minmax(180px,1fr) auto; }
  `]
})
export class GrowthSeoProfilesComponent implements OnInit {
  private readonly service = inject(GrowthSearchService);
  private readonly distribution = inject(GrowthDistributionService);
  private readonly marketing = inject(GrowthMarketingService);
  private readonly i18n = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  readonly loading = signal(false);
  readonly profiles = signal<GrowthSeoProfile[]>([]);
  readonly dialogVisible = signal(false);
  selectedId: string | null = null;
  model = { title: '', targetType: 'content-asset', targetRefId: null as string | null, description: '', status: 'Draft' };
  keywordRows: TextRow[] = [{ value: '' }];
  metaRows: KeyValueRow[] = [{ key: '', value: '' }];
  schemaRows: KeyValueRow[] = [{ key: '@type', value: 'WebPage' }];
  readonly statusOptions = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Active', label: 'Active' },
    { value: 'NeedsReview', label: 'Needs review' },
    { value: 'Approved', label: 'Approved' }
  ];

  ngOnInit(): void { this.load(); }

  targetTypeOptions() {
    return [
      { value: 'content-asset', label: this.t('growth.search.seo.targetTypes.contentAsset') },
      { value: 'landing-page', label: this.t('growth.search.seo.targetTypes.landingPage') },
      { value: 'collection-page', label: this.t('growth.search.seo.targetTypes.collectionPage') },
      { value: 'product', label: this.t('growth.search.seo.targetTypes.productOffering') }
    ];
  }

  targetTypeLabel(value: string): string {
    return this.targetTypeOptions().find(item => item.value === value)?.label ?? value;
  }

  load(): void {
    this.loading.set(true);
    this.service.listSeoProfiles().subscribe({
      next: rows => {
        this.profiles.set(rows);
        this.loading.set(false);
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
          const match = rows.find(item => item.id === id);
          if (match) {
            this.edit(match);
          }
        }
      },
      error: () => this.loading.set(false)
    });
  }

  openDialog(): void {
    this.selectedId = null;
    this.model = { title: '', targetType: 'content-asset', targetRefId: null, description: '', status: 'Draft' };
    this.keywordRows = [{ value: '' }];
    this.metaRows = [{ key: '', value: '' }];
    this.schemaRows = [{ key: '@type', value: 'WebPage' }];
    this.dialogVisible.set(true);
  }

  edit(row: GrowthSeoProfile): void {
    this.selectedId = row.id;
    this.model = {
      title: row.title,
      targetType: row.targetType,
      targetRefId: row.targetRefId,
      description: row.description,
      status: row.status
    };
    this.keywordRows = this.parseStringArray(row.keywordsJson).map(value => ({ value }));
    this.metaRows = this.parseObjectEntries(row.metaJson);
    this.schemaRows = this.parseObjectEntries(row.schemaOrgJson);
    if (this.keywordRows.length === 0) this.keywordRows = [{ value: '' }];
    if (this.metaRows.length === 0) this.metaRows = [{ key: '', value: '' }];
    if (this.schemaRows.length === 0) this.schemaRows = [{ key: '@type', value: 'WebPage' }];
    this.dialogVisible.set(true);
  }

  save(): void {
    this.service.upsertSeoProfile({
      id: this.selectedId,
      title: this.model.title,
      targetType: this.model.targetType,
      targetRefId: this.model.targetRefId || null,
      description: this.model.description,
      keywordsJson: JSON.stringify(this.keywordRows.map(row => row.value.trim()).filter(Boolean)),
      metaJson: JSON.stringify(this.rowsToObject(this.metaRows)),
      schemaOrgJson: JSON.stringify(this.rowsToObject(this.schemaRows)),
      status: this.model.status
    }).subscribe({
      next: () => {
        this.dialogVisible.set(false);
        this.load();
      }
    });
  }

  addKeyword(): void { this.keywordRows.push({ value: '' }); }
  removeKeyword(index: number): void { this.keywordRows.splice(index, 1); }
  addMeta(): void { this.metaRows.push({ key: '', value: '' }); }
  removeMeta(index: number): void { this.metaRows.splice(index, 1); }
  addSchema(): void { this.schemaRows.push({ key: '', value: '' }); }
  removeSchema(index: number): void { this.schemaRows.splice(index, 1); }

  private rowsToObject(rows: KeyValueRow[]): Record<string, string> {
    const object: Record<string, string> = {};
    rows.forEach(row => {
      const key = row.key.trim();
      if (!key) return;
      object[key] = row.value.trim();
    });
    return object;
  }

  private parseStringArray(json: string): string[] {
    try {
      const parsed = JSON.parse(json) as unknown;
      return Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') as string[] : [];
    } catch {
      return [];
    }
  }

  private parseObjectEntries(json: string): KeyValueRow[] {
    try {
      const parsed = JSON.parse(json) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
      return Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({ key, value: value == null ? '' : String(value) }));
    } catch {
      return [];
    }
  }

  runAudit(row: GrowthSeoProfile): void {
    this.service.runSeoAudit(row.id, {
      providerMode: 'sandbox',
      score: row.score ?? 0.72,
      findingsJson: '[{"rule":"title-length","status":"ok"}]',
      recommendationsJson: '[{"action":"expand-faq-schema"}]'
    }).subscribe({ next: () => this.load() });
  }

  createStorefrontApply(row: GrowthSeoProfile): void {
    this.service.getStorefrontApplySuggestion(row.id).subscribe({
      next: suggestion => {
        const submitApply = (contentAssetId: string) => {
          this.distribution.createStorefrontApplyRequest({
            contentAssetId,
            targetType: suggestion.targetType,
            seoProfileId: row.id,
            offeringRefId: suggestion.offeringRefId ?? (row.targetType === 'product' ? row.targetRefId : null),
            requestPayloadJson: suggestion.requestPayloadJson,
            idempotencyKey: `seo-sf-${row.id}-${Date.now()}`,
            correlationId: crypto.randomUUID()
          }).subscribe();
        };

        if (suggestion.contentAssetId) {
          this.marketing.ensureContentReadyForStorefrontApply(suggestion.contentAssetId).subscribe({
            next: asset => submitApply(asset.id)
          });
          return;
        }

        if (row.targetType === 'product') {
          this.marketing.listContent().subscribe(contents => {
            const approved = contents.find(item => this.isApprovedContent(item));
            if (approved) {
              this.marketing.ensureContentReadyForStorefrontApply(approved.id).subscribe({
                next: asset => submitApply(asset.id)
              });
            }
          });
        }
      }
    });
  }

  private isApprovedContent(item: GrowthContentAsset): boolean {
    return this.marketing.isStorefrontApplyContentReady(item.status);
  }

  t(key: string): string { return this.i18n.translate(key); }
}
